import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadGSPN, uploadQless, uploadGD, uploadOneStop, uploadAprovacoes, uploadBacklog, uploadStockRepair } from '../api';
import AprovacoesImportModal from './AprovacoesImportModal';

const UPLOAD_TYPES = [
  { key: 'gspn', label: 'GSPN ACC', desc: 'Base principal de ordens de serviço', fn: null },
  { key: 'qless', label: 'Qless', desc: 'Senhas de atendimento', fn: null },
  { key: 'gd', label: 'GD', desc: 'Guias de devolução', fn: null },
  { key: 'one_stop', label: 'One Stop', desc: 'Frete / Logística', fn: null },
  { key: 'aprovacoes', label: 'Aprovações', desc: 'Planilha Sales Day / Aprovações', fn: null },
  { key: 'backlog', label: 'Backlog', desc: 'ServiceOrderList — OS abertas', fn: null },
  { key: 'stock_repair', label: 'Stock Repair (WTY)', desc: 'Relatório WTY — volume dedicado do SR', fn: null },
];

// Map de funções (não pode ser inline no array por causa do ascCode/nomeLoja)
const UPLOAD_FNS = {
  gspn: (file, ascCode, nomeLoja) => uploadGSPN(file, ascCode, nomeLoja),
  qless: (file, ascCode, nomeLoja) => uploadQless(file, ascCode, nomeLoja),
  gd: (file, ascCode, nomeLoja) => uploadGD(file, ascCode, nomeLoja),
  one_stop: (file, ascCode, nomeLoja) => uploadOneStop(file, ascCode, nomeLoja),
  aprovacoes: (file, ascCode, nomeLoja, empresaSlug) => uploadAprovacoes(file, empresaSlug, ascCode, nomeLoja),
  backlog: (file, ascCode, nomeLoja) => uploadBacklog(file, ascCode, nomeLoja),
  stock_repair: (file, ascCode, nomeLoja) => uploadStockRepair(file, ascCode, nomeLoja),
};

// Upload types ocultos por empresa
const HIDDEN_UPLOADS = {
  'fix-csp': ['one_stop'],
  'fix-msc': ['qless', 'gd', 'one_stop'],
  'vip': ['one_stop'],
};

function UploadPanel({ onUploadComplete, ascCode = '2326126', nomeLoja = 'Tatuapé', empresaSlug = '' }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('gspn');
  const [aprovBundleOpen, setAprovBundleOpen] = useState(false);

  const onDrop = useCallback(async (files) => {
    if (files.length === 0) return;

    // Aprovações usa modal de bundle (não dropzone direto)
    if (selectedType === 'aprovacoes') {
      setAprovBundleOpen(true);
      return;
    }

    setLoading(true);

    const uploadType = UPLOAD_TYPES.find(t => t.key === selectedType);
    const file = files[0];

    try {
      let res;
      res = await UPLOAD_FNS[selectedType](file, ascCode, nomeLoja);
      setResults(prev => [{
        tipo: uploadType.label,
        arquivo: file.name,
        ...res.data,
        success: true,
        timestamp: new Date().toLocaleTimeString(),
      }, ...prev]);
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      setResults(prev => [{
        tipo: uploadType.label,
        arquivo: file.name,
        success: false,
        error: err.response?.data?.detail || err.message,
        timestamp: new Date().toLocaleTimeString(),
      }, ...prev]);
    }

    setLoading(false);
  }, [selectedType, onUploadComplete, ascCode, nomeLoja, empresaSlug]);

  const acceptTypes = selectedType === 'aprovacoes'
    ? {
        'text/csv': ['.csv'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'text/html': ['.htm', '.html'],
      }
    : {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls'],
        'text/csv': ['.csv'],
      };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptTypes,
    multiple: false,
  });

  return (
    <div className="upload-panel">
      <h2>Upload de Planilhas — {nomeLoja}</h2>

      <div className="upload-type-selector">
        {UPLOAD_TYPES.filter(t => !(HIDDEN_UPLOADS[empresaSlug] || []).includes(t.key)).map(t => (
          <button
            key={t.key}
            className={`type-btn ${selectedType === t.key ? 'active' : ''}`}
            onClick={() => {
              setSelectedType(t.key);
              if (t.key === 'aprovacoes') setAprovBundleOpen(true);
            }}
          >
            <strong>{t.label}</strong>
            <small>{t.desc}</small>
          </button>
        ))}
      </div>

      {selectedType !== 'aprovacoes' && (
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${loading ? 'loading' : ''}`}
        >
          <input {...getInputProps()} />
          {loading ? (
            <div className="spinner">Processando...</div>
          ) : isDragActive ? (
            <p>Solte o arquivo aqui...</p>
          ) : (
            <p>Arraste a planilha <strong>{UPLOAD_TYPES.find(t => t.key === selectedType)?.label}</strong> aqui ou clique para selecionar</p>
          )}
        </div>
      )}

      {selectedType === 'aprovacoes' && (
        <div
          onClick={() => setAprovBundleOpen(true)}
          className="dropzone"
          style={{ cursor: 'pointer' }}
        >
          <p>Clique para abrir o modal de import de Aprovações (multi-arquivo)</p>
        </div>
      )}

      <AprovacoesImportModal
        open={aprovBundleOpen}
        onClose={() => setAprovBundleOpen(false)}
        empresaSlug={empresaSlug}
        ascCode={ascCode}
        nomeLoja={nomeLoja}
        onSuccess={(data) => {
          // Adiciona ao histórico de resultados
          const resultados = data.resultados || {};
          const newResults = Object.entries(resultados).map(([tipo, r]) => ({
            tipo: tipo === 'aprovacoes' ? 'Aprovações' : tipo === 'pecas' ? 'Peças' : tipo === 'mao_obra' ? 'Mão de Obra' : 'Notas',
            arquivo: r.arquivo || `(${tipo})`,
            linhas_novas: r.linhas_inseridas ?? r.linhas_novas,
            linhas_duplicadas: r.linhas_substituidas ?? r.linhas_atualizadas,
            success: true,
            timestamp: new Date().toLocaleTimeString(),
          }));
          setResults(prev => [...newResults, ...prev]);
          if (onUploadComplete) onUploadComplete();
        }}
      />

      {results.length > 0 && (
        <div className="upload-results">
          <h3>Resultados</h3>
          {results.map((r, i) => (
            <div key={i} className={`result-item ${r.success ? 'success' : 'error'}`}>
              <div className="result-header">
                <span className="result-badge">{r.tipo}</span>
                <span className="result-time">{r.timestamp}</span>
              </div>
              <div className="result-file">{r.arquivo}</div>
              {r.success ? (
                <div className="result-stats">
                  <span>Inseridas: <strong>{r.linhas_novas || r.linhas_processadas}</strong></span>
                  {(r.linhas_duplicadas || 0) > 0 && (
                    <span title="Registros anteriores que existiam dos meses presentes na planilha e foram substituídos">
                      Substituídas: <strong>{r.linhas_duplicadas}</strong>
                    </span>
                  )}
                </div>
              ) : (
                <div className="result-error">{r.error}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UploadPanel;
